import { Controller } from "@hotwired/stimulus";

const PROGRESS_STEPS = new Set([
  "reading_pdf",
  "extracting_xml",
  "parsing_xml",
  "validating_xsd",
  "validating_schematron",
  "building_report",
]);

const RESULT_EVENTS = {
  valid: "factur_x_validator_result_passed",
  warning: "factur_x_validator_result_warning",
  simple: "factur_x_validator_result_simple_pdf",
};

const CHECK_PRESENTATION = {
  passed: { icon: "✓", color: "bg-sxn-light-green" },
  failed: { icon: "×", color: "bg-sxn-pink" },
  warning: { icon: "!", color: "bg-sxn-light-yellow" },
  not_checked: { icon: "—", color: "bg-white" },
};

const DATA_GROUPS = [
  "pdf",
  "context",
  "document",
  "seller",
  "buyer",
  "agreement",
  "delivery",
  "lines",
  "taxes",
  "totals",
  "payment",
  "other",
];

// French labels for the CII element names shown in the field-by-field table.
// English readers get the humanised element name instead — the CII vocabulary
// is English to begin with, so "GrandTotalAmount" reads as "Grand Total Amount".
const DATA_FIELD_LABELS_FR = {
  "@currencyID": "Devise du montant",
  "@format": "Format de date",
  "@schemeAgencyID": "Agence du schéma",
  "@schemeID": "Schéma d’identifiant",
  "@unitCode": "Unité de mesure",
  AllowanceTotalAmount: "Total des remises",
  BasisAmount: "Base taxable",
  BasisQuantity: "Quantité de base du prix",
  BilledQuantity: "Quantité facturée",
  BuyerReference: "Référence acheteur",
  CalculatedAmount: "Montant de TVA calculé",
  CategoryCode: "Catégorie de TVA",
  ChargeAmount: "Prix ou montant unitaire",
  ChargeTotalAmount: "Total des frais",
  CityName: "Ville",
  Content: "Contenu de note",
  CountryID: "Code pays",
  DateTimeString: "Date",
  Description: "Description",
  DuePayableAmount: "Net à payer",
  ExemptionReason: "Motif d’exonération",
  ExemptionReasonCode: "Code d’exonération",
  GlobalID: "Identifiant global",
  GrandTotalAmount: "Total TTC",
  IBANID: "IBAN",
  ID: "Identifiant",
  InvoiceCurrencyCode: "Devise de la facture",
  IssuerAssignedID: "Référence attribuée par l’émetteur",
  LineID: "Numéro de ligne",
  LineOne: "Adresse — ligne 1",
  LineThree: "Adresse — ligne 3",
  LineTotalAmount: "Total des lignes HT",
  LineTwo: "Adresse — ligne 2",
  Name: "Nom",
  OccurrenceDateTime: "Date de réalisation",
  PaymentReference: "Référence de paiement",
  PostcodeCode: "Code postal",
  RateApplicablePercent: "Taux de TVA",
  RoundingAmount: "Montant d’arrondi",
  SubjectCode: "Code objet de la note",
  TaxBasisTotalAmount: "Total HT taxable",
  TaxTotalAmount: "Total de TVA",
  TotalPrepaidAmount: "Total déjà payé",
  TypeCode: "Code de type",
  URIID: "Adresse électronique",
};

const CONTEXTUAL_FIELD_LABELS = {
  fr: {
    "AssociatedDocumentLineDocument.LineID": "Numéro de ligne",
    "BusinessProcessSpecifiedDocumentContextParameter.ID": "Processus métier",
    "ExchangedDocument.ID": "Numéro de facture",
    "ExchangedDocument.TypeCode": "Type de facture",
    "GuidelineSpecifiedDocumentContextParameter.ID": "Profil Factur-X",
    "SpecifiedLegalOrganization.ID": "Identifiant légal",
    "SpecifiedTaxRegistration.ID": "Numéro fiscal",
  },
  en: {
    "AssociatedDocumentLineDocument.LineID": "Line number",
    "BusinessProcessSpecifiedDocumentContextParameter.ID": "Business process",
    "ExchangedDocument.ID": "Invoice number",
    "ExchangedDocument.TypeCode": "Invoice type",
    "GuidelineSpecifiedDocumentContextParameter.ID": "Factur-X profile",
    "SpecifiedLegalOrganization.ID": "Legal identifier",
    "SpecifiedTaxRegistration.ID": "Tax registration number",
  },
};

export default class extends Controller {
  static targets = [
    "announcement",
    "buyer",
    "checks",
    "dataEmptyCount",
    "dataFieldCount",
    "dataFilter",
    "dataGroups",
    "dataPanel",
    "dropzone",
    "errorMessage",
    "errorTitle",
    "fileMeta",
    "fileName",
    "findings",
    "input",
    "invoiceNumber",
    "issueDate",
    "lineCount",
    "panel",
    "profile",
    "progressLabel",
    "seller",
    "totalHT",
    "totalTTC",
    "warningCount",
  ];

  connect() {
    this.analysisSequence = 0;
    this.show("idle");
  }

  disconnect() {
    this.analysisSequence += 1;
  }

  choose() {
    this.inputTarget.click();
  }

  fileSelected(event) {
    const [file] = event.target.files;
    if (file) this.run(file);
  }

  dragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("bg-black/[0.04]", "translate-y-[-2px]");
  }

  dragLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return;

    this.clearDropzoneHighlight();
  }

  drop(event) {
    event.preventDefault();
    this.clearDropzoneHighlight();

    const [file] = event.dataTransfer.files;
    if (file) this.run(file);
  }

  reset() {
    this.analysisSequence += 1;
    this.inputTarget.value = "";
    this.clearRenderedData();
    this.show("idle");
    this.track("factur_x_validator_reset");
    document
      .querySelector("#validator")
      ?.scrollIntoView({ behavior: "smooth" });
  }

  trackEvent(event) {
    this.track(event.params.event);
  }

  // The analyzer (plus the official XSD validator) is the heavy part of this
  // page. Loading it on demand keeps it out of the site-wide bundle — esbuild
  // emits it as its own chunk (see esbuild.config.js).
  async analyzer() {
    this.analyzerModule ||= await import("../factur_x/analyzer.js");
    return this.analyzerModule;
  }

  async run(file) {
    const sequence = ++this.analysisSequence;

    this.clearRenderedData();
    this.updateFileDetails(file.name, "");
    this.show("loading");
    this.revealPanel("center");
    this.track("factur_x_validator_started");

    try {
      const { analyzeFacturX, setLocale, t } = await this.analyzer();
      setLocale(document.documentElement.lang);
      this.i18n = t;

      this.updateFileDetails(file.name, this.formatFileMeta(file));
      this.updateProgress("reading_pdf");
      this.announce(t("announce.started", { filename: file.name }));

      const report = await analyzeFacturX(file, (step) => {
        if (sequence === this.analysisSequence) this.updateProgress(step);
      });

      if (sequence !== this.analysisSequence) return;

      this.renderReport(file, report);
      this.show(report.verdict);
      this.track(RESULT_EVENTS[report.verdict]);
      this.visiblePanel?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error) {
      if (sequence !== this.analysisSequence) return;

      this.renderError(error);
      this.show("unsupported");
      this.track("factur_x_validator_result_unsupported");
      this.visiblePanel?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }

  renderReport(file, report) {
    const profile = report.profile?.name || this.t("profile.unknown");
    const pageLabel = this.t(
      report.pdf.pages > 1 ? "report.pages.plural" : "report.pages",
      { count: report.pdf.pages },
    );
    const detail =
      report.verdict === "simple"
        ? `${pageLabel} · ${this.t("report.no_xml")}`
        : `${pageLabel} · ${profile}`;

    this.updateFileDetails(
      file.name,
      `${this.formatFileSize(file.size)} · ${detail}`,
    );

    if (report.verdict === "valid") {
      this.profileTarget.textContent = this.t("report.profile", { profile });
      this.renderSummary(report.summary);
    }

    if (report.verdict === "valid" || report.verdict === "warning") {
      // The check list carries the "not executed" scope (PDF/A, Schematron),
      // which matters most to the reader whose file has anomalies.
      this.renderChecks(report.checks);
    }

    if (report.verdict === "warning") {
      this.renderFindings(report.findings);
      const count = report.findings.length;
      this.warningCountTarget.textContent = this.t(
        count === 1 ? "report.findings.one" : "report.findings.other",
        { count },
      );
    }

    if (report.verdict === "valid" || report.verdict === "warning") {
      this.renderDataAudit(report);
    }

    this.announce(this.verdictAnnouncement(report, profile));
  }

  verdictAnnouncement(report, profile) {
    if (report.verdict === "valid") {
      return this.t("announce.valid", { profile });
    }

    if (report.verdict === "warning") {
      const count = report.findings.length;
      return this.t(
        count === 1 ? "announce.warning.one" : "announce.warning.other",
        { count },
      );
    }

    return this.t("announce.simple");
  }

  // Rendered once per list: both the "passed" and the "warning" panel carry a
  // check list, and a DOM node can only live in one of them at a time.
  renderChecks(checks) {
    this.checksTargets.forEach((list) => {
      list.replaceChildren(...checks.map((item) => this.buildCheckRow(item)));
    });
  }

  buildCheckRow(item) {
    const presentation =
      CHECK_PRESENTATION[item.status] || CHECK_PRESENTATION.warning;
    const row = document.createElement("li");
    row.className =
      "grid grid-cols-[2rem_1fr_auto] items-center gap-3 px-4 py-3 text-sm";

    const icon = document.createElement("span");
    icon.className = `flex h-7 w-7 items-center justify-center rounded-full border border-black ${presentation.color} text-xs font-bold`;
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = presentation.icon;

    const label = document.createElement("span");
    label.className = "font-semibold";
    label.textContent = item.label;

    const detail = document.createElement("span");
    detail.className = "text-right text-xs text-gray-600";
    detail.textContent = item.detail || this.t(`status.${item.status}`);

    row.append(icon, label, detail);
    return row;
  }

  renderFindings(findings) {
    this.findingsTarget.replaceChildren(
      ...findings.map((finding) => {
        const row = document.createElement("li");
        row.className = "grid gap-2 p-4 sm:grid-cols-[7rem_1fr]";

        const code = document.createElement("span");
        code.className =
          "h-fit w-fit rounded border border-black bg-white px-2 py-1 font-mono text-xs font-bold";
        code.textContent = finding.code;

        const content = document.createElement("div");
        const title = document.createElement("p");
        title.className = "text-sm font-semibold";
        title.textContent = finding.title;

        const detail = document.createElement("p");
        detail.className = "mt-1 text-xs leading-relaxed text-gray-600";
        detail.textContent = finding.detail;

        content.append(title, detail);
        row.append(code, content);
        return row;
      }),
    );
  }

  renderSummary(summary = {}) {
    this.invoiceNumberTarget.textContent = this.textOrDash(
      summary.invoiceNumber,
    );
    this.issueDateTarget.textContent = this.formatDate(summary.issueDate);
    this.sellerTarget.textContent = this.textOrDash(summary.seller);
    this.buyerTarget.textContent = this.textOrDash(summary.buyer);
    this.totalHTTarget.textContent = this.formatMoney(
      summary.taxBasisTotal ?? summary.lineTotal,
      summary.currency,
    );
    this.totalTTCTarget.textContent = this.formatMoney(
      summary.grandTotal,
      summary.currency,
    );
    this.lineCountTarget.textContent = Number.isInteger(summary.lineCount)
      ? String(summary.lineCount)
      : "—";
  }

  renderDataAudit(report) {
    const pdf = report.pdf || {};
    const xmp = pdf.xmp || {};
    const attachment = report.attachment || {};
    const profile = report.profile || {};
    const xmlFields = report.dataFields || [];

    const xmpPath = (key) => this.t("row.path.xmp", { key });
    const attachmentPath = this.t("row.path.attachment");
    const localPath = this.t("row.path.local");

    this.dataAuditRows = [
      this.dataRow(
        "pdf",
        this.t("row.pages"),
        pdf.pages,
        this.t("row.path.pdf_pages"),
        "pages",
      ),
      this.dataRow(
        "pdf",
        this.t("row.pdfa_declaration"),
        pdf.pdfaDeclaration,
        xmpPath("pdfaid"),
        "pdfaDeclaration",
      ),
      this.dataRow(
        "pdf",
        this.t("row.document_type"),
        xmp.documentType,
        xmpPath("fx:DocumentType"),
        "fx:DocumentType",
      ),
      this.dataRow(
        "pdf",
        this.t("row.document_filename"),
        xmp.documentFilename,
        xmpPath("fx:DocumentFileName"),
        "fx:DocumentFileName",
      ),
      this.dataRow(
        "pdf",
        this.t("row.version"),
        xmp.version,
        xmpPath("fx:Version"),
        "fx:Version",
      ),
      this.dataRow(
        "pdf",
        this.t("row.conformance_level"),
        xmp.conformanceLevel,
        xmpPath("fx:ConformanceLevel"),
        "fx:ConformanceLevel",
      ),
      this.dataRow(
        "pdf",
        this.t("row.attachment_filename"),
        attachment.filename,
        attachmentPath,
        "attachment.filename",
      ),
      this.dataRow(
        "pdf",
        this.t("row.attachment_size"),
        Number.isFinite(attachment.byteSize)
          ? this.formatFileSize(attachment.byteSize)
          : null,
        attachmentPath,
        "attachment.byteSize",
      ),
      this.dataRow(
        "context",
        this.t("row.profile_name"),
        profile.name,
        localPath,
        "profile.name",
      ),
      this.dataRow(
        "context",
        this.t("row.profile_id"),
        profile.id,
        localPath,
        "profile.id",
      ),
      ...xmlFields,
    ];
    this.xmlEmptyFieldCount = xmlFields.filter(
      (field) => field.kind === "element" && field.empty,
    ).length;
    this.dataAuditQuery = "";
    this.dataFilterTarget.value = "";
    this.renderDataAuditRows();
  }

  dataRow(group, label, value, path, name) {
    const normalizedValue =
      value === null || value === undefined ? "" : String(value);

    return {
      group,
      label,
      name,
      value: normalizedValue,
      path,
      kind: "metadata",
      empty: normalizedValue.length === 0,
    };
  }

  filterData(event) {
    this.dataAuditQuery = event.currentTarget.value;
    this.renderDataAuditRows();
  }

  renderDataAuditRows() {
    const query = this.normalizeSearch(this.dataAuditQuery);
    const rows = (this.dataAuditRows || []).filter((row) => {
      if (!query) return true;

      return this.normalizeSearch(
        [
          this.dataFieldLabel(row),
          row.name,
          row.value,
          row.path,
          this.dataGroupLabel(row.group),
        ].join(" "),
      ).includes(query);
    });

    this.dataFieldCountTarget.textContent = query
      ? this.t("data.count.filtered", {
          shown: rows.length,
          total: this.dataAuditRows.length,
        })
      : this.t("data.count.all", { count: this.dataAuditRows.length });
    this.dataEmptyCountTarget.textContent = this.t(
      this.xmlEmptyFieldCount === 1 ? "data.empty.one" : "data.empty.other",
      { count: this.xmlEmptyFieldCount },
    );

    if (rows.length === 0) {
      const empty = document.createElement("p");
      empty.className =
        "rounded-lg border border-black bg-white p-5 text-sm text-gray-600";
      empty.textContent = this.t("data.no_match");
      this.dataGroupsTarget.replaceChildren(empty);
      return;
    }

    const sections = DATA_GROUPS.flatMap((group) => {
      const groupRows = rows.filter((row) => row.group === group);
      return groupRows.length > 0
        ? [this.buildDataGroup(group, this.dataGroupLabel(group), groupRows)]
        : [];
    });

    this.dataGroupsTarget.replaceChildren(...sections);
  }

  buildDataGroup(group, title, rows) {
    const section = document.createElement("section");
    section.className =
      "overflow-hidden rounded-lg border border-black bg-white";
    section.dataset.dataGroup = group;

    const heading = document.createElement("div");
    heading.className =
      "flex items-center justify-between gap-3 border-b border-black/20 bg-black/[0.03] px-4 py-3";

    const titleElement = document.createElement("h5");
    titleElement.className = "font-unbounded text-xs font-bold";
    titleElement.textContent = title;

    const count = document.createElement("span");
    count.className =
      "rounded-full border border-black bg-white px-2 py-0.5 text-[0.68rem] font-bold";
    count.textContent = this.t(
      rows.length > 1 ? "data.fields.plural" : "data.fields",
      { count: rows.length },
    );
    heading.append(titleElement, count);

    const wrapper = document.createElement("div");
    wrapper.className = "overflow-x-auto";

    const table = document.createElement("table");
    table.className = "w-full min-w-[46rem] table-fixed text-left text-xs";

    const head = document.createElement("thead");
    head.className = "bg-black/[0.03] text-[0.68rem] uppercase tracking-wide";
    const headRow = document.createElement("tr");
    [
      [this.t("data.column.field"), "w-[26%]"],
      [this.t("data.column.value"), "w-[34%]"],
      [this.t("data.column.path"), "w-[40%]"],
    ].forEach(([label, width]) => {
      const cell = document.createElement("th");
      cell.className = `${width} border-b border-black/20 px-4 py-2 font-bold`;
      cell.scope = "col";
      cell.textContent = label;
      headRow.append(cell);
    });
    head.append(headRow);

    const body = document.createElement("tbody");
    body.className = "divide-y divide-black/10";
    body.append(...rows.map((row) => this.buildDataRow(row)));

    table.append(head, body);
    wrapper.append(table);
    section.append(heading, wrapper);
    return section;
  }

  buildDataRow(row) {
    const tableRow = document.createElement("tr");
    tableRow.className = row.empty ? "bg-sxn-light-pink/30" : "";

    const labelCell = document.createElement("th");
    labelCell.className = "px-4 py-3 align-top font-normal";
    labelCell.scope = "row";

    const label = document.createElement("span");
    label.className = "block font-bold";
    label.textContent = this.dataFieldLabel(row);

    const xmlName = document.createElement("code");
    xmlName.className =
      "mt-1 block break-all font-mono text-[0.68rem] text-gray-500";
    xmlName.textContent = row.name;
    labelCell.append(label, xmlName);

    const valueCell = document.createElement("td");
    valueCell.className = "px-4 py-3 align-top";
    if (row.empty) {
      const empty = document.createElement("span");
      // Un conteneur que le schéma impose vide n'est pas une donnée manquante :
      // le teinter comme les autres fait chercher un défaut inexistant.
      empty.className = row.structural
        ? "inline-flex rounded-full border border-black bg-white px-2 py-0.5 text-[0.68rem] font-bold"
        : "inline-flex rounded-full border border-black bg-sxn-pink px-2 py-0.5 text-[0.68rem] font-bold";
      empty.textContent = this.t(
        row.structural
          ? "data.value.empty_by_design"
          : row.kind === "element"
            ? "data.value.empty_element"
            : "data.value.not_found",
      );
      valueCell.append(empty);
    } else {
      const value = document.createElement("span");
      value.className =
        "block whitespace-pre-wrap break-words font-mono leading-relaxed";
      value.textContent = row.value;
      valueCell.append(value);
    }

    const pathCell = document.createElement("td");
    pathCell.className =
      "break-all px-4 py-3 align-top font-mono text-[0.68rem] leading-relaxed text-gray-600";
    pathCell.textContent = row.path;

    tableRow.append(labelCell, valueCell, pathCell);
    return tableRow;
  }

  dataGroupLabel(group) {
    return this.t(`data.group.${group}`);
  }

  dataFieldLabel(row) {
    if (row.label) return row.label;

    const segments = row.path.split("/").filter(Boolean);
    const parent = segments.at(-2)?.replace(/\[\d+\]$/, "");
    const contextual =
      CONTEXTUAL_FIELD_LABELS[this.locale]?.[`${parent}.${row.name}`];
    const translated = this.locale === "fr" ? DATA_FIELD_LABELS_FR[row.name] : null;

    return (
      contextual ||
      translated ||
      row.name
        .replace(/^@/, "")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/^./, (character) => character.toUpperCase())
    );
  }

  get locale() {
    return document.documentElement.lang?.toLowerCase().startsWith("en")
      ? "en"
      : "fr";
  }

  // Translations live in the lazily loaded analyzer chunk; before it resolves
  // the markup still shows the server-rendered copy.
  t(key, params) {
    return this.i18n ? this.i18n(key, params) : "";
  }

  normalizeSearch(value = "") {
    return String(value)
      .normalize("NFD")
      .replaceAll(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();
  }

  renderError(error) {
    // `this.analyzerModule` is undefined when the chunk itself failed to load,
    // in which case the error is never one of ours.
    const analysisError = this.analyzerModule?.FacturXAnalysisError;
    if (!analysisError) {
      // Chunk never loaded: keep the server-rendered copy already in the panel.
      return;
    }

    const knownError = error instanceof analysisError;
    this.errorTitleTarget.textContent = this.t(
      knownError ? "error.title.known" : "error.title.unknown",
    );
    this.errorMessageTarget.textContent = knownError
      ? error.publicMessage
      : this.t("error.fallback");
    this.announce(
      `${this.errorTitleTarget.textContent} ${this.errorMessageTarget.textContent}`,
    );
  }

  announce(message) {
    this.announcementTarget.textContent = message;
  }

  show(state) {
    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.validatorState !== state);
    });
    this.dataPanelTarget.classList.toggle(
      "hidden",
      state !== "valid" && state !== "warning",
    );
  }

  updateProgress(step) {
    this.progressLabelTarget.textContent = this.t(
      PROGRESS_STEPS.has(step) ? `progress.${step}` : "progress.default",
    );
  }

  updateFileDetails(name, meta) {
    this.fileNameTargets.forEach((target) => {
      target.textContent = name;
    });
    this.fileMetaTargets.forEach((target) => {
      target.textContent = meta;
    });
  }

  formatFileMeta(file) {
    return `${this.formatFileSize(file.size)} · ${this.t("meta.local_analysis")}`;
  }

  formatFileSize(size) {
    return size >= 1_000_000
      ? `${(size / 1_000_000).toFixed(1)} ${this.t("unit.mb")}`
      : `${Math.max(1, Math.round(size / 1_000))} ${this.t("unit.kb")}`;
  }

  get intlLocale() {
    return this.locale === "en" ? "en-GB" : "fr-FR";
  }

  formatMoney(value, currency = "EUR") {
    if (!Number.isFinite(value)) return "—";

    try {
      return new Intl.NumberFormat(this.intlLocale, {
        style: "currency",
        currency: currency || "EUR",
      }).format(value);
    } catch {
      return `${new Intl.NumberFormat(this.intlLocale).format(value)} ${currency || ""}`.trim();
    }
  }

  formatDate(value) {
    const match = value?.match(/^(\d{4})-?(\d{2})-?(\d{2})$/);
    if (!match) return this.textOrDash(value);

    const [, year, month, day] = match;
    return new Intl.DateTimeFormat(this.intlLocale, {
      dateStyle: "long",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
  }

  textOrDash(value) {
    return value || "—";
  }

  clearDropzoneHighlight() {
    this.dropzoneTarget.classList.remove(
      "bg-black/[0.04]",
      "translate-y-[-2px]",
    );
  }

  clearRenderedData() {
    this.announce("");
    this.fileNameTargets.forEach((target) => {
      target.textContent = "";
    });
    this.fileMetaTargets.forEach((target) => {
      target.textContent = "";
    });
    [
      this.invoiceNumberTarget,
      this.issueDateTarget,
      this.sellerTarget,
      this.buyerTarget,
      this.totalHTTarget,
      this.totalTTCTarget,
      this.lineCountTarget,
      this.profileTarget,
      this.warningCountTarget,
    ].forEach((target) => {
      target.textContent = "";
    });
    this.checksTargets.forEach((list) => list.replaceChildren());
    this.findingsTarget.replaceChildren();
    this.dataAuditRows = [];
    this.dataAuditQuery = "";
    this.xmlEmptyFieldCount = 0;
    this.dataFilterTarget.value = "";
    this.dataFieldCountTarget.textContent = "";
    this.dataEmptyCountTarget.textContent = "";
    this.dataGroupsTarget.replaceChildren();
  }

  track(name) {
    if (name) window.sa_event?.(name);
  }

  get visiblePanel() {
    return this.panelTargets.find(
      (panel) => !panel.classList.contains("hidden"),
    );
  }

  // La zone de dépôt se trouve bas dans la page : après un dépôt, le panneau
  // qui la remplace peut rester hors écran et l'analyse semble ne rien faire.
  // On n'amène le panneau à l'écran que s'il n'y est pas déjà — faire défiler
  // quelqu'un qui regarde déjà le bon endroit est plus gênant qu'utile.
  revealPanel(block = "start") {
    const panel = this.visiblePanel;
    if (!panel) return;

    const { top, bottom } = panel.getBoundingClientRect();
    const fullyVisible = top >= 0 && bottom <= window.innerHeight;
    if (fullyVisible) return;

    panel.scrollIntoView({ behavior: "smooth", block });
  }
}
